/**
 * Hierarquia de ranks (CLAUDE.md §5).
 *
 * 10 níveis, do Estagiário Júnior ao Mestre Geek Lendário. A progressão
 * de XP cresce de forma quase exponencial nos níveis altos para que a
 * conquista do rank máximo signifique algo.
 *
 * `accentCss` é só uma sugestão de cor por rank — pode ser usado pela UI
 * para tematizar o avatar/badge no rank correspondente.
 */

export interface Rank {
  /** 1..10 */
  level: number;
  name: string;
  /** XP necessário para entrar neste rank. */
  xpRequired: number;
  /** Cor sugerida em CSS (#RRGGBB) para badges/avatar. */
  accentCss: string;
}

export const RANKS: readonly Rank[] = [
  { level: 1, name: 'Estagiário Júnior', xpRequired: 0, accentCss: '#9b9bff' },
  { level: 2, name: 'Estagiário Pleno', xpRequired: 500, accentCss: '#06b6d4' },
  { level: 3, name: 'Dev Iniciante', xpRequired: 1500, accentCss: '#06b6d4' },
  { level: 4, name: 'Dev Júnior', xpRequired: 3000, accentCss: '#22c55e' },
  { level: 5, name: 'Dev Pleno', xpRequired: 6000, accentCss: '#22c55e' },
  { level: 6, name: 'Dev Sênior', xpRequired: 10000, accentCss: '#facc15' },
  { level: 7, name: 'Tech Lead', xpRequired: 15000, accentCss: '#facc15' },
  { level: 8, name: 'Arquiteto Geek', xpRequired: 25000, accentCss: '#7c3aed' },
  { level: 9, name: 'CTO Mirim', xpRequired: 40000, accentCss: '#ef4444' },
  { level: 10, name: 'Mestre Geek Lendário', xpRequired: 60000, accentCss: '#fbbf24' },
];

/** Rank atual a partir do XP total acumulado. */
export function getRankForXP(xp: number): Rank {
  const safeXp = Math.max(0, xp);
  let current = RANKS[0]!;
  for (const rank of RANKS) {
    if (safeXp >= rank.xpRequired) current = rank;
    else break;
  }
  return current;
}

/** Próximo rank (null se já está no topo). */
export function getNextRank(xp: number): Rank | null {
  const current = getRankForXP(xp);
  return RANKS.find((r) => r.level === current.level + 1) ?? null;
}

export interface RankProgress {
  current: Rank;
  next: Rank | null;
  /** XP acima do limiar do rank atual. */
  xpIntoCurrent: number;
  /** XP necessário para chegar ao próximo rank a partir do atual. */
  xpToNext: number;
  /** Razão 0..1 de progresso para o próximo rank (1 se já no topo). */
  ratio: number;
}

export function getRankProgress(xp: number): RankProgress {
  const safeXp = Math.max(0, xp);
  const current = getRankForXP(safeXp);
  const next = getNextRank(safeXp);
  const xpIntoCurrent = safeXp - current.xpRequired;
  if (!next) {
    return { current, next: null, xpIntoCurrent, xpToNext: 0, ratio: 1 };
  }
  const xpToNext = next.xpRequired - current.xpRequired;
  return {
    current,
    next,
    xpIntoCurrent,
    xpToNext,
    ratio: xpToNext === 0 ? 1 : Math.min(1, xpIntoCurrent / xpToNext),
  };
}
